// src/components/ContactList.js

import { BrandButton, TextCancelButton } from '../button';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
  Checkbox,
  Tooltip,
  Rating,
  Grid,
  LinearProgress
} from '@mui/material';
import { acme, acmePageSectionCardSx, acmeBillingSectionContentSx } from '../../theme/acmeTheme';
import {
  PageShell,
  PageHeader,
  PageBlockTitle,
  PageBlockHeader,
  Card,
  CardContent,
  Table,
  Dialog,
  Button as AcmeButton,
  Menu,
  MenuItem as AcmeMenuItem,
  TableEmptyRow,
  TableActionsMenu,
  TableActionsMenuButton,
  destructiveMenuItemSx,
  stopTableRowClick,
} from "../ui";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import GroupIcon from '@mui/icons-material/Group';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import axios from 'axios';
import * as XLSX from 'xlsx';
import Pagination from '@mui/material/Pagination';
import AddIcon from '@mui/icons-material/Add';
import { io } from 'socket.io-client';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import { isAdminRole } from '../../utils/userRoles';

const CATEGORIES = [
  'Construction', 
  'IT', 
  'Recruiting', 
  'Marketing', 
  'Finance', 
  'Healthcare', 
  'Education',
  'Manufacturing',
  'Retail',
  'Hospitality',
  'Transportation',
  'Telecommunications',
  'Energy',
  'Consulting',
  'Other'
];

const VALIDITY_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'valid', label: 'Valid' },
  { value: 'invalid', label: 'Invalid' },
  { value: 'risky', label: 'Risky' },
  { value: 'pending', label: 'Pending' },
  { value: 'suppressed', label: 'Suppressed' },
  { value: 'unknown', label: 'Unknown' },
];

const ContactList = () => {
  const { groupName } = useParams();  
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);

  // State for notifications
  const { setSuccessMessage, setErrorMessage, snackbarProps } = usePageSnackbar();

  // State for groups and filtering
  const [groups, setGroups] = useState([]);
  const [groupNameFilter, setGroupNameFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [filteredGroups, setFilteredGroups] = useState([]);

  // States for import dialogs
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importedContacts, setImportedContacts] = useState([]);
  const [importGroupName, setImportGroupName] = useState('');
  const [importCategory, setImportCategory] = useState('');
  const [customImportCategory, setCustomImportCategory] = useState('');
  const [importProgress, setImportProgress] = useState(0);
  const [importMessage, setImportMessage] = useState('');
  const [showProgress, setShowProgress] = useState(false);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupCategory, setNewGroupCategory] = useState('');
  const [customNewGroupCategory, setCustomNewGroupCategory] = useState('');
  
  // State for group deletion confirmation
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState({ 
    open: false, 
    type: null,
    message: '',
    groupId: null,
    contactId: null,
    contactEmail: null
  });
  const [groupsMenuAnchor, setGroupsMenuAnchor] = useState(null);
  const [contactsMenuAnchor, setContactsMenuAnchor] = useState(null);
  const [groupRowMenu, setGroupRowMenu] = useState({ anchorEl: null, group: null });
  const [contactRowMenu, setContactRowMenu] = useState({ anchorEl: null, contact: null });
  const [renameGroupDialog, setRenameGroupDialog] = useState({
    open: false,
    currentName: '',
    newName: '',
  });
  const [renamingGroup, setRenamingGroup] = useState(false);

  // States for contacts in a group
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({
    email: '',
    status: '',
    validity: '',
  });
  
   // Состояния для пагинации группы и контактов (0-indexed)
  const [groupsPage, setGroupsPage] = useState(0);
  const [groupsRowsPerPage, setGroupsRowsPerPage] = useState(20);

  const [contactsPage, setContactsPage] = useState(0);
  const [contactsRowsPerPage, setContactsRowsPerPage] = useState(50);

  // States for adding/importing emails into group
  const [openAddEmailDialog, setOpenAddEmailDialog] = useState(false);
  const [newEmails, setNewEmails] = useState('');
  const [openImportGroupDialog, setOpenImportGroupDialog] = useState(false);
  const [importedGroupContacts, setImportedGroupContacts] = useState([]);

  // States for loading indicators
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingAddEmails, setLoadingAddEmails] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);

  // States for selection checkboxes
  const [selectedGroups, setSelectedGroups] = useState([]); // array of group names
  const [selectedContacts, setSelectedContacts] = useState([]); // array of contact IDs

  // States for contact rating system
  const [isUpdatingRatings, setIsUpdatingRatings] = useState(false);
  const [, setRatingStats] = useState(null);

  // States for new import system
  const [importMethodDialog, setImportMethodDialog] = useState(false);
  const [importType, setImportType] = useState(''); // 'groups' or 'contacts'
  const [pasteImportDialog, setPasteImportDialog] = useState(false);
  const [pasteData, setPasteData] = useState('');

  useEffect(() => {
    if (!groupName) {
      fetchGroups();
    } else {
      fetchContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch fns defined below; groupName drives load
  }, [groupName]);

  useEffect(() => {
    // Get user role from session
    const getUserRole = async () => {
      try {
        const response = await axios.get('/api/auth/check-session');
        if (response.data && response.data.user) {
          setUserRole(response.data.user.role);
        }
      } catch (error) {
        console.error('Failed to get user role:', error);
      }
    };
    getUserRole();
  }, []);

  // Fetch groups
  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const response = await axios.get('/api/contacts/groups');
      console.log('Fetched groups:', response.data);
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups:', error.message);
      setErrorMessage(`Failed to fetch groups: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Fetch contacts for a group
  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const response = await axios.get(`/api/contacts/groups/${encodeURIComponent(groupName)}`);
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts', error);
      setErrorMessage('Failed to fetch contacts.');
    } finally {
      setLoadingContacts(false);
    }
  };

  // Handle file selection for group import
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target.result;
      let rawEmails = [];
      
      try {
        if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
          // Handle CSV/TXT files
          const lines = data.split('\n');
          rawEmails = lines
            .map(line => line.trim())
            .filter(line => line && line.includes('@'))
            .map(email => email.replace(/[",;]/g, '').trim());
        } else {
          // Handle Excel files
          const workbook = XLSX.read(new Uint8Array(data), { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const imported = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          rawEmails = imported.map(row => row[0]).filter(email => email);
        }
        
        if (rawEmails.length === 0) {
          setErrorMessage('No valid email addresses found in the file.');
          return;
        }

        // Normalize and deduplicate emails
        const normalizedEmails = [...new Set(rawEmails.map(email => email.toLowerCase().trim()))];
        
        if (normalizedEmails.length === 0) {
          setErrorMessage('No valid email addresses found after normalization.');
          return;
        }

        console.log(`Original emails: ${rawEmails.length}, after deduplication: ${normalizedEmails.length}`);
        
        setImportedContacts(normalizedEmails.map(email => ({ email })));
        setOpenImportDialog(true);
      } catch (error) {
        console.error('Error reading file:', error);
        setErrorMessage('Error reading file. Please check the file format.');
      }
    };
    
    if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  // Import contacts to create a new group
  const handleImportSubmit = async () => {
    setLoadingImport(true);
    
    const finalCategory = importCategory === 'Other' ? customImportCategory : importCategory;
    
    if (!importGroupName || !finalCategory) {
      setErrorMessage('Please specify group name and category.');
      setLoadingImport(false);
      return;
    }
    if (importedContacts.length === 0) {
      setErrorMessage('No contacts to import.');
      setLoadingImport(false);
      return;
    }

    // Более надежная обработка контактов
    const formattedEmails = importedContacts
      .map(contact => {
        try {
          if (typeof contact === "string") {
            return contact.trim().toLowerCase();
          }
          if (typeof contact === "object" && contact && contact.email) {
            return contact.email.trim().toLowerCase();
          }
          return null;
        } catch (error) {
          console.error('Error processing contact:', contact, error);
          return null;
        }
      })
      .filter(email => email && email !== "" && email.includes('@'));

    if (formattedEmails.length === 0) {
      setErrorMessage('No valid email addresses found after processing.');
      setLoadingImport(false);
      return;
    }

    console.log(`Processing ${formattedEmails.length} emails for import`);

    // Показываем прогресс для всех импортов
    setShowProgress(true);
    setImportProgress(0);
    setImportMessage(`Starting import of ${formattedEmails.length} contacts...`);

    try {
      const response = await axios.post('/api/contacts/import', {
        group: importGroupName,  
        category: finalCategory,  
        emails: formattedEmails,
      }, {
        timeout: 300000 // 5 минут таймаут для больших импортов
      });
      
      setOpenImportDialog(false);
      setImportedContacts([]);
      setImportGroupName('');
      setImportCategory('');
      setCustomImportCategory('');
      setShowProgress(false);
      setImportProgress(0);
      setImportMessage('');
      setSuccessMessage(`Contacts imported successfully: ${response.data.added} added, ${response.data.skipped} skipped.`);
      fetchGroups();
    } catch (error) {
      console.error('Failed to import contacts:', error);
      if (error.code === 'ECONNABORTED') {
        setErrorMessage('Import timed out. Please try with a smaller file or contact support.');
      } else if (error.response?.data?.error) {
        setErrorMessage(`Failed to import contacts: ${error.response.data.error}`);
      } else {
        setErrorMessage('Failed to import contacts. Please try again.');
      }
    } finally {
      setLoadingImport(false);
    }
  };

  const handleCreateGroup = async () => {
    setLoadingGroups(true);
    
    const finalCategory = newGroupCategory === 'Other' ? customNewGroupCategory : newGroupCategory;
    
    if (!newGroupName.trim() || !finalCategory.trim()) {
      setErrorMessage('Please enter a valid group name and category.');
      setLoadingGroups(false);
      return;
    }
    try {
      await axios.post('/api/contacts/groups', { name: newGroupName, category: finalCategory });
      setOpenCreateDialog(false);
      setNewGroupName('');
      setNewGroupCategory('');
      setCustomNewGroupCategory('');
      setSuccessMessage('Group created successfully.');
      fetchGroups();
    } catch (error) {
      console.error('Failed to create group:', error);
      setErrorMessage('Failed to create group.');
    } finally {
      setLoadingGroups(false);
    }
  };

  // Delete group
  const handleDeleteGroup = async () => {
    if (!confirmDeleteDialog.groupId) return;
    setLoadingGroups(true);
    try {
      await axios.delete(`/api/contacts/groups/${encodeURIComponent(confirmDeleteDialog.groupId.trim())}`);
      setConfirmDeleteDialog({ open: false, groupId: null });
      setSuccessMessage('Group deleted successfully.');
      fetchGroups();
    } catch (error) {
      console.error('Failed to delete group', error);
      setErrorMessage('Failed to delete group.');
    } finally {
      setLoadingGroups(false);
    }
  };

  const openRenameGroupDialog = (currentName) => {
    setRenameGroupDialog({
      open: true,
      currentName,
      newName: currentName,
    });
  };

  const closeRenameGroupDialog = () => {
    setRenameGroupDialog({ open: false, currentName: '', newName: '' });
  };

  const handleRenameGroup = async () => {
    const trimmedNewName = renameGroupDialog.newName.trim();
    const currentName = renameGroupDialog.currentName.trim();

    if (!trimmedNewName) {
      setErrorMessage('Group name is required.');
      return;
    }
    if (trimmedNewName === currentName) {
      setErrorMessage('New name must be different from the current name.');
      return;
    }

    setRenamingGroup(true);
    try {
      await axios.put(
        `/api/contacts/groups/${encodeURIComponent(currentName)}/rename`,
        { newName: trimmedNewName },
      );
      setSuccessMessage(`Group renamed to "${trimmedNewName}".`);
      closeRenameGroupDialog();

      if (groupName && groupName === currentName) {
        navigate(`/contacts/${encodeURIComponent(trimmedNewName)}`, {
          state: location.state,
        });
      } else {
        fetchGroups();
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Failed to rename group.');
    } finally {
      setRenamingGroup(false);
    }
  };

  const openGroupRowMenu = (event, group) => {
    event.stopPropagation();
    setGroupRowMenu({ anchorEl: event.currentTarget, group });
  };

  const closeGroupRowMenu = () => {
    setGroupRowMenu({ anchorEl: null, group: null });
  };

  const handleGroupRowMenuItem = (action) => {
    const group = groupRowMenu.group;
    if (!group) return;
    closeGroupRowMenu();

    if (action === 'reverify') {
      axios
        .post(`/api/contacts/groups/${encodeURIComponent(group.groupName)}/reverify`)
        .then(() => {
          setSuccessMessage(`Verification started for "${group.groupName}"`);
          fetchGroups();
        })
        .catch((err) => {
          setErrorMessage(err.response?.data?.error || 'Failed to start verification');
        });
      return;
    }
    if (action === 'rename') {
      openRenameGroupDialog(group.groupName);
      return;
    }
    if (action === 'delete') {
      setConfirmDeleteDialog({
        open: true,
        type: 'group',
        message: `Are you sure you want to delete the group "${group.groupName}"? This will permanently delete all ${group.totalCount} contacts in this group. This action cannot be undone.`,
        groupId: group.groupName,
        contactId: null,
        contactEmail: null,
      });
    }
  };

  const openContactRowMenu = (event, contact) => {
    event.stopPropagation();
    setContactRowMenu({ anchorEl: event.currentTarget, contact });
  };

  const closeContactRowMenu = () => {
    setContactRowMenu({ anchorEl: null, contact: null });
  };

  const handleContactRowMenuItem = (action) => {
    const contact = contactRowMenu.contact;
    if (!contact) return;
    closeContactRowMenu();

    if (action === 'rename') {
      openRenameGroupDialog(groupName);
      return;
    }
    if (action === 'delete') {
      handleDeleteContact(contact._id, contact.email);
    }
  };

  // Show delete contact confirmation dialog
  const handleDeleteContact = (id, email) => {
    setConfirmDeleteDialog({
      open: true,
      type: 'contact',
      message: `Are you sure you want to delete the contact "${email}"? This action cannot be undone.`,
      contactId: id,
      contactEmail: email,
      groupId: null
    });
  };

  // Actually delete contact
  const confirmDeleteContact = async () => {
    if (!confirmDeleteDialog.contactId) return;
    try {
      await axios.delete(`/api/contacts/${confirmDeleteDialog.contactId}`);
      setSuccessMessage('Contact deleted successfully.');
      fetchContacts();
    } catch (error) {
      console.error('Failed to delete contact:', error);
      setErrorMessage('Failed to delete contact.');
    }
  };

  // Filters for contacts
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  // Filter contacts based on search and filters
  const filteredContacts = contacts.filter((contact) => {
    const contactValidity = contact.validity || 'pending';
    return (
      contact.email.includes(search) &&
      (filter.email ? contact.email.includes(filter.email) : true) &&
      (filter.status ? contact.status === filter.status : true) &&
      (filter.validity ? contactValidity === filter.validity : true)
    );
  });

  const paginatedGroups = filteredGroups.slice(
    groupsPage * groupsRowsPerPage,
    groupsPage * groupsRowsPerPage + groupsRowsPerPage
  );

  const paginatedContacts = filteredContacts.slice(
    contactsPage * contactsRowsPerPage,
    contactsPage * contactsRowsPerPage + contactsRowsPerPage
  );
  
  const updateContactStatus = async (id, newStatus) => {
    try {
      if (newStatus === 'unsubscribed') {
        await axios.post('/api/contacts/unsubscribe', { id }); 
      } else {
        await axios.patch(`/api/contacts/${id}/status`, { status: newStatus });
      }
      setContacts((prevContacts) =>
        prevContacts.map((contact) =>
          contact._id === id ? { ...contact, status: newStatus } : contact
        )
      );
      setSuccessMessage('Status updated successfully.');
    } catch (error) {
      console.error('Failed to update contact status:', error);
      if (error.response) {
        setErrorMessage(`Failed to update contact status: ${error.response.data.error || error.message}`);
      } else if (error.request) {
        setErrorMessage('No response from server. Please try again later.');
      } else {
        setErrorMessage(`Error: ${error.message}`);
      }
    }
  };

  // Add emails manually to an existing group
  const handleAddEmails = async () => {
    setLoadingAddEmails(true);
    const emailsArray = newEmails.split(',').map(email => email.trim()).filter(email => email !== '');
    if (emailsArray.length === 0) {
      setErrorMessage('Please enter at least one valid email.');
      setLoadingAddEmails(false);
      return;
    }
    const invalidEmails = emailsArray.filter(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmails.length > 0) {
      setErrorMessage(`Invalid email formats: ${invalidEmails.join(', ')}`);
      setLoadingAddEmails(false);
      return;
    }
    try {
      await axios.post('/api/contacts/groups/add-emails', {
        groupName,
        emails: emailsArray,
      });
      setSuccessMessage('Emails added successfully.');
      setOpenAddEmailDialog(false);
      setNewEmails('');
      fetchContacts();
    } catch (error) {
      console.error('Failed to add emails:', error);
      setErrorMessage(`Failed to add emails: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoadingAddEmails(false);
    }
  };

  // Handle file selection for importing contacts to a group
  const handleFileSelectGroupImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target.result;
      let rawEmails = [];
      
      try {
        if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
          // Handle CSV/TXT files
          const lines = data.split('\n');
          rawEmails = lines
            .map(line => line.trim())
            .filter(line => line && line.includes('@'))
            .map(email => email.replace(/[",;]/g, '').trim());
        } else {
          // Handle Excel files
          const workbook = XLSX.read(new Uint8Array(data), { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const imported = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          rawEmails = imported.map(row => row[0]).filter(email => email);
        }
        
        if (rawEmails.length === 0) {
          setErrorMessage('No valid email addresses found in the file.');
          return;
        }

        // Normalize and deduplicate emails
        const normalizedEmails = [...new Set(rawEmails.map(email => email.toLowerCase().trim()))];
        
        if (normalizedEmails.length === 0) {
          setErrorMessage('No valid email addresses found after normalization.');
          return;
        }

        console.log(`Original emails: ${rawEmails.length}, after deduplication: ${normalizedEmails.length}`);
        
        setImportedGroupContacts(normalizedEmails.map(email => ({ email })));
        setOpenImportGroupDialog(true);
      } catch (error) {
        console.error('Error reading file:', error);
        setErrorMessage('Error reading file. Please check the file format.');
      }
    };
    
    if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  // Import contacts into an existing group
  const handleImportGroupSubmit = async () => {
    setLoadingImport(true);
    if (importedGroupContacts.length === 0) {
      setErrorMessage('No contacts to import.');
      setLoadingImport(false);
      return;
    }

    // Более надежная обработка контактов
    const formattedEmails = importedGroupContacts
      .map(contact => {
        try {
          if (typeof contact === "string") {
            return contact.trim().toLowerCase();
          }
          if (typeof contact === "object" && contact && contact.email) {
            return contact.email.trim().toLowerCase();
          }
          return null;
        } catch (error) {
          console.error('Error processing contact:', contact, error);
          return null;
        }
      })
      .filter(email => email && email !== "" && email.includes('@'));

    if (formattedEmails.length === 0) {
      setErrorMessage('No valid email addresses found after processing.');
      setLoadingImport(false);
      return;
    }

    // Валидация email-адресов
    const invalidEmails = formattedEmails.filter(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmails.length > 0) {
      setErrorMessage(`Invalid email formats: ${invalidEmails.slice(0, 5).join(', ')}${invalidEmails.length > 5 ? '...' : ''}`);
      setLoadingImport(false);
      return;
    }

    console.log(`Processing ${formattedEmails.length} emails for group import`);

    // Показываем прогресс для всех импортов
    setShowProgress(true);
    setImportProgress(0);
    setImportMessage(`Starting import of ${formattedEmails.length} contacts to group "${groupName}"...`);

    try {
      const response = await axios.post('/api/contacts/groups/import-emails', {
        groupName,
        emails: formattedEmails,
      }, {
        timeout: 300000 // 5 минут таймаут для больших импортов
      });
      setOpenImportGroupDialog(false);
      setImportedGroupContacts([]);
      setShowProgress(false);
      setImportProgress(0);
      setImportMessage('');
      setSuccessMessage(`Contacts imported successfully: ${response.data.added} added, ${response.data.skipped} skipped.`);
      fetchContacts();
    } catch (error) {
      console.error('Failed to import contacts:', error);
      setShowProgress(false);
      setImportProgress(0);
      setImportMessage('');
      if (error.code === 'ECONNABORTED') {
        setErrorMessage('Import timed out. Please try with a smaller file or contact support.');
      } else if (error.response?.data?.error) {
        setErrorMessage(`Failed to import contacts: ${error.response.data.error}`);
      } else {
        setErrorMessage('Failed to import contacts. Please try again.');
      }
    } finally {
      setLoadingImport(false);
    }
  };

  // Group filtering logic
  const applyGroupFilters = () => {
    let tempGroups = [...groups];
    if (groupNameFilter.trim() !== '') {
      tempGroups = tempGroups.filter(group =>
        group.groupName.toLowerCase().includes(groupNameFilter.toLowerCase())
      );
    }
    if (categoryFilter.trim() !== '') {
      tempGroups = tempGroups.filter(group => group.category === categoryFilter);
    }
    setFilteredGroups(tempGroups);
  };

  useEffect(() => {
    applyGroupFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync filtered list from filters
  }, [groups, groupNameFilter, categoryFilter]);

  // EXPORT FUNCTIONS

  // Export groups into one Excel file with one sheet per group
  const handleExportGroups = async () => {
    try {
      const groupsToExport = selectedGroups.length > 0 
        ? filteredGroups.filter(group => selectedGroups.includes(group.groupName))
        : filteredGroups;
      
      const exportData = await Promise.all(groupsToExport.map(async (group) => {
        const response = await axios.get(`/api/contacts/groups/${encodeURIComponent(group.groupName)}`);
        return { groupName: group.groupName, contacts: response.data };
      }));

      const workbook = XLSX.utils.book_new();
      exportData.forEach(item => {
        const sheetName = item.groupName.length > 31 ? item.groupName.substring(0, 31) : item.groupName;
        
        // Подготавливаем данные контактов для экспорта с правильными рейтингами
        const formattedContacts = (item.contacts || []).map(contact => ({
          email: contact.email,
          status: contact.status,
          rating: contact.rating !== null && contact.rating !== undefined ? contact.rating : 0,
          group: contact.group,
          category: contact.category,
          country: contact.country || '',
          createdAt: contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '',
          updatedAt: contact.updatedAt ? new Date(contact.updatedAt).toLocaleDateString() : ''
        }));
        
        const ws = XLSX.utils.json_to_sheet(formattedContacts);
        XLSX.utils.book_append_sheet(workbook, ws, sheetName);
      });
      XLSX.writeFile(workbook, 'ContactGroups.xlsx');
    } catch (error) {
      console.error('Export groups failed:', error);
      setErrorMessage('Failed to export groups.');
    }
  };

  // Export contacts for current group view into Excel
  const handleExportContacts = () => {
    const contactsToExport = selectedContacts.length > 0 
      ? contacts.filter(contact => selectedContacts.includes(contact._id))
      : filteredContacts;
    
    // Подготавливаем данные для экспорта с включением Rating колонки
    const exportData = contactsToExport.map(contact => ({
      email: contact.email,
      status: contact.status,
      rating: contact.rating !== null && contact.rating !== undefined ? contact.rating : 0, // Используем реальный рейтинг или 0
      group: contact.group,
      category: contact.category,
      country: contact.country || '',
      createdAt: contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '',
      updatedAt: contact.updatedAt ? new Date(contact.updatedAt).toLocaleDateString() : ''
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, groupName);
    XLSX.writeFile(wb, `${groupName}_Contacts.xlsx`);
  };

  // Add these new functions after other handler functions
  const handleDeleteSelectedGroups = async () => {
    try {
      await axios.post('/api/contacts/groups/delete-multiple', {
        groupNames: selectedGroups
      });
      setSuccessMessage(`Successfully deleted ${selectedGroups.length} groups`);
      setSelectedGroups([]);
      fetchGroups();
    } catch (error) {
      console.error('Failed to delete groups:', error);
      setErrorMessage('Failed to delete selected groups');
    }
  };

  const handleDeleteSelectedContacts = async () => {
    try {
      await axios.post('/api/contacts/delete-multiple', {
        contactIds: selectedContacts
      });
      setSuccessMessage(`Successfully deleted ${selectedContacts.length} contacts`);
      setSelectedContacts([]);
      fetchContacts();
    } catch (error) {
      console.error('Failed to delete contacts:', error);
      setErrorMessage('Failed to delete selected contacts');
    }
  };

  // Handle back button navigation with pagination state
  const handleBackToGroups = () => {
    // Получаем сохраненную страницу из location state или устанавливаем 0
    const returnPage = location.state?.returnPage || 0;
    setGroupsPage(returnPage);
    navigate('/contacts');
  };

  // Handle rating system functions
  const handleUpdateRatings = async () => {
    setIsUpdatingRatings(true);
    try {
      const response = await axios.post('/api/contacts/update-ratings?limit=1000');
      setSuccessMessage(`updated ratings: ${response.data.updatedCount}`);
      
      // Обновляем контакты после обновления рейтингов
      if (groupName) {
        await fetchContacts();
      } else {
        await fetchGroups();
      }
    } catch (error) {
      console.error('Failed to update ratings:', error);
      setErrorMessage('Failed to update ratings');
    } finally {
      setIsUpdatingRatings(false);
    }
  };

  const fetchRatingStats = async () => {
    try {
      const response = await axios.get('/api/contacts/rating-stats');
      setRatingStats(response.data);
    } catch (error) {
      console.error('Failed to fetch rating stats:', error);
    }
  };

  // New import method handlers
  const handleImportMethodSelect = (method, type) => {
    setImportType(type);
    setImportMethodDialog(false);
    
    if (method === 'file') {
      // Trigger file input click
      if (type === 'groups') {
        document.getElementById('file-input-groups').click();
      } else {
        document.getElementById('file-input-contacts').click();
      }
    } else if (method === 'paste') {
      setPasteImportDialog(true);
    }
  };

  const handlePasteImport = () => {
    if (!pasteData.trim()) {
      setErrorMessage('Please paste some data to import.');
      return;
    }

    // Parse paste data (assuming emails are separated by newlines, commas, or semicolons)
    const rawEmails = pasteData
      .split(/[\n,;]/)
      .map(email => email.trim())
      .filter(email => email && email.includes('@'));

    if (rawEmails.length === 0) {
      setErrorMessage('No valid email addresses found in the pasted data.');
      return;
    }

    // Normalize and deduplicate emails
    const normalizedEmails = [...new Set(rawEmails.map(email => email.toLowerCase().trim()))];
    
    if (normalizedEmails.length === 0) {
      setErrorMessage('No valid email addresses found after normalization.');
      return;
    }

    console.log(`Original emails: ${rawEmails.length}, after deduplication: ${normalizedEmails.length}`);

    if (importType === 'groups') {
      // For groups import - set imported contacts and open import dialog
      setImportedContacts(normalizedEmails.map(email => ({ email })));
      setOpenImportDialog(true);
    } else {
      // For contacts import - set imported contacts and open group import dialog
      setImportedGroupContacts(normalizedEmails.map(email => ({ email })));
      setOpenImportGroupDialog(true);
    }

    setPasteImportDialog(false);
    setPasteData('');
  };

  // WebSocket для получения прогресса импорта
  useEffect(() => {
    const socket = io();
    
    socket.on('import_progress', (data) => {
      setImportProgress(data.progress);
      setImportMessage(data.message);
      setShowProgress(true);

      if (data.progress === 100) {
        setTimeout(() => {
          setShowProgress(false);
          setImportProgress(0);
          setImportMessage('');
        }, 2000);
      }
    });

    socket.on('verification_progress', (data) => {
      setImportProgress(data.progress ?? 0);
      setImportMessage(
        data.message || `Verifying ${data.groupName || 'group'}…`,
      );
      setShowProgress(true);

      if (data.status === 'ready' || data.progress === 100) {
        fetchGroups();
        setTimeout(() => {
          setShowProgress(false);
          setImportProgress(0);
          setImportMessage('');
        }, 2500);
      }
      if (data.status === 'failed') {
        setErrorMessage(data.message || 'Group verification failed');
        fetchGroups();
      }
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- socket listeners registered once on mount
  }, []);

  // Fetch rating stats on component mount
  useEffect(() => {
    if (!groupName) {
      fetchRatingStats();
    }
  }, [groupName]);

  // --- UI for Groups List ---
  const renderGroupsUI = () => (
    <Card sx={{ ...acmePageSectionCardSx, boxShadow: 'none', mt: 0 }}>
      <CardContent sx={acmeBillingSectionContentSx}>
      <PageBlockHeader>
        <PageBlockTitle>Contact Groups</PageBlockTitle>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {selectedGroups.length > 0 && (
            <AcmeButton
              variant="outline"
              onClick={() => {
                setConfirmDeleteDialog({
                  open: true,
                  type: 'groups',
                  message: `Are you sure you want to delete ${selectedGroups.length} selected groups? This will permanently delete all contacts in these groups. This action cannot be undone.`,
                  groupId: null,
                  contactId: null,
                  contactEmail: null
                });
              }}
              sx={{ color: acme.colors.destructive, borderColor: acme.colors.destructive }}
            >
              Delete Selected ({selectedGroups.length})
            </AcmeButton>
          )}
          <AcmeButton
            variant="default"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
            disabled={loadingGroups}
          >
            New Group
          </AcmeButton>
          <AcmeButton
            variant="outline"
            startIcon={<CloudUploadIcon />}
            onClick={() => {
              setImportType('groups');
              setImportMethodDialog(true);
            }}
            disabled={loadingGroups}
          >
            Import Contacts
          </AcmeButton>
          <TableActionsMenuButton
            ariaLabel="More actions"
            onClick={(e) => setGroupsMenuAnchor(e.currentTarget)}
          />
          <Menu
            anchorEl={groupsMenuAnchor}
            open={Boolean(groupsMenuAnchor)}
            onClose={() => setGroupsMenuAnchor(null)}
          >
            <AcmeMenuItem
              onClick={() => {
                setGroupsMenuAnchor(null);
                handleExportGroups();
              }}
            >
              <FileDownloadIcon fontSize="small" sx={{ mr: 1.5, color: acme.colors.iconDefault }} />
              Export Groups
            </AcmeMenuItem>
            <AcmeMenuItem
              onClick={() => {
                setGroupsMenuAnchor(null);
                handleUpdateRatings();
              }}
              disabled={isUpdatingRatings}
            >
              <StarIcon fontSize="small" sx={{ mr: 1.5, color: acme.colors.iconDefault }} />
              {isUpdatingRatings ? 'Updating...' : 'Update Ratings'}
            </AcmeMenuItem>
          </Menu>
          <input 
            id="file-input-groups"
            type="file" 
            accept=".xlsx, .xls, .csv, .txt" 
            hidden 
            onChange={handleFileSelect} 
          />
        </Box>
      </PageBlockHeader>
      
      <Table containerSx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedGroups.length === filteredGroups.length && filteredGroups.length > 0}
                  indeterminate={selectedGroups.length > 0 && selectedGroups.length < filteredGroups.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedGroups(filteredGroups.map(group => group.groupName));
                    } else {
                      setSelectedGroups([]);
                    }
                  }}
                />
              </TableCell>
              <TableCell>Group Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Verification</TableCell>
              <TableCell>Contacts (valid / total)</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>
                <TextField
                  value={groupNameFilter}
                  onChange={(e) => setGroupNameFilter(e.target.value)}
                  placeholder="Filter by Group Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  displayEmpty
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell sx={{ py: 1, borderBottom: `1px solid ${acme.colors.border}` }} />
              <TableCell sx={{ py: 1, borderBottom: `1px solid ${acme.colors.border}` }} />
              <TableCell sx={{ p: 0, borderBottom: `1px solid ${acme.colors.border}` }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedGroups.length > 0 ? (
              paginatedGroups.map((group) => {
                const isSelected = selectedGroups.includes(group.groupName);
                return (
                  <TableRow
                    key={group.groupName}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? acme.colors.muted : 'inherit',
                      '&:hover': {
                        backgroundColor: acme.colors.muted,
                      },
                    }}
                    onClick={(e) => {
                      if (e.target.type !== 'checkbox') {
                        navigate(`/contacts/${group.groupName}`, { 
                          state: { returnPage: groupsPage } 
                        });
                      }
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (e.target.checked) {
                            setSelectedGroups((prev) => [...prev, group.groupName]);
                          } else {
                            setSelectedGroups((prev) => prev.filter(name => name !== group.groupName));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>{group.groupName}</TableCell>
                    <TableCell>{group.category}</TableCell>
                    <TableCell>
                      {group.verificationStatus === 'ready' ? (
                        <span style={{ color: acme.colors.brand }}>Ready</span>
                      ) : group.verificationStatus === 'verifying' ||
                        group.verificationStatus === 'queued' ? (
                        <span style={{ color: '#ed6c02' }}>
                          {group.verificationStatus === 'queued'
                            ? 'Queued'
                            : `Verifying ${group.verificationProgress ?? 0}%`}
                        </span>
                      ) : group.verificationStatus === 'failed' ? (
                        <span style={{ color: 'red' }}>Failed</span>
                      ) : (
                        <span>—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span style={{ color: acme.colors.brand, fontWeight: 600 }}>
                        {group.validCount ?? 0}
                      </span>
                      {' / '}
                      <span>{group.totalCount ?? 0}</span>
                      <span style={{ color: '#666', fontSize: '0.85em' }}>
                        {' '}
                        (sendable: {group.sendableCount ?? 0})
                      </span>
                    </TableCell>
                    <TableCell onClick={stopTableRowClick}>
                      <TableActionsMenuButton
                        ariaLabel="Group actions"
                        onClick={(event) => openGroupRowMenu(event, group)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableEmptyRow
                colSpan={6}
                icon={GroupIcon}
                title="No contact groups yet"
                description="Create your first contact group to organize your email addresses by category. You can import contacts from Excel files or add them manually for targeted email campaigns."
                hint="Start by creating a new group or importing your contact list."
              />
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={filteredGroups.length}
                page={groupsPage}
                onPageChange={(event, newPage) => setGroupsPage(newPage)}
                rowsPerPage={groupsRowsPerPage}
                onRowsPerPageChange={(event) => {
                  const newVal = parseInt(event.target.value, 10);
                  setGroupsRowsPerPage(newVal);
                  setGroupsPage(0);
                }}
                rowsPerPageOptions={[20, 50, 100, 200, 500]}
                labelRowsPerPage="Rows per page"
              />
            </TableRow>
          </TableFooter>
      </Table>

      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)}>
        <DialogTitle>Create New Group</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Group Name"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
          />
          <Select
            fullWidth
            value={newGroupCategory}
            onChange={(e) => setNewGroupCategory(e.target.value)}
            displayEmpty
            style={{ marginTop: '15px' }}
          >
            <MenuItem value="" disabled>Select Category</MenuItem>
            {CATEGORIES.map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
          {newGroupCategory === 'Other' && (
            <TextField
              fullWidth
              margin="dense"
              label="Custom Category"
              value={customNewGroupCategory}
              onChange={(e) => setCustomNewGroupCategory(e.target.value)}
              placeholder="Enter your custom category"
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <TextCancelButton onClick={() => setOpenCreateDialog(false)} disabled={loadingGroups}>Cancel</TextCancelButton>
          <BrandButton onClick={handleCreateGroup} disabled={loadingGroups}>
            {loadingGroups ? <CircularProgress size={24} /> : 'Create'}
          </BrandButton>
        </DialogActions>
      </Dialog>
      
      <Dialog open={openImportDialog} onClose={() => setOpenImportDialog(false)}>
        <DialogTitle>Import Contacts</DialogTitle>
        <DialogContent>
          <Typography>You are about to import {importedContacts.length} contacts.</Typography>
          
          {/* Progress Bar */}
          {showProgress && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {importMessage}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {importProgress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={importProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: acme.colors.brand,
                    borderRadius: 4
                  }
                }} 
              />
            </Box>
          )}
          
          <TextField
            fullWidth
            margin="dense"
            label="Group Name"
            value={importGroupName}
            onChange={(e) => setImportGroupName(e.target.value)}
          />
          <Select
            fullWidth
            value={importCategory}
            onChange={(e) => setImportCategory(e.target.value)}
            displayEmpty
            style={{marginTop: '15px'}}
          >
            <MenuItem value="" disabled>Select Category</MenuItem>
            {CATEGORIES.map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
          {importCategory === 'Other' && (
            <TextField
              fullWidth
              margin="dense"
              label="Custom Category"
              value={customImportCategory}
              onChange={(e) => setCustomImportCategory(e.target.value)}
              placeholder="Enter your custom category"
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <TextCancelButton onClick={() => setOpenImportDialog(false)} disabled={loadingImport}>Cancel</TextCancelButton>
          <BrandButton onClick={handleImportSubmit} disabled={loadingImport}>
            {loadingImport ? <CircularProgress size={24} /> : 'Add Contacts'}
          </BrandButton>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteDialog.open} onClose={() => setConfirmDeleteDialog({open: false, type: null, message: '', groupId: null, contactId: null, contactEmail: null})}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <Typography>{confirmDeleteDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDeleteDialog({open: false, type: null, message: '', groupId: null, contactId: null, contactEmail: null})} 
            disabled={loadingGroups || loadingContacts}
          >
            Cancel
          </Button>
          <Button 
            color="error" 
            onClick={() => {
              if (confirmDeleteDialog.type === 'groups') {
                handleDeleteSelectedGroups();
              } else if (confirmDeleteDialog.type === 'contacts') {
                handleDeleteSelectedContacts();
              } else if (confirmDeleteDialog.type === 'contact') {
                confirmDeleteContact();
              } else if (confirmDeleteDialog.type === 'group') {
                handleDeleteGroup();
              }
              setConfirmDeleteDialog({open: false, type: null, message: '', groupId: null, contactId: null, contactEmail: null});
            }} 
            disabled={loadingGroups || loadingContacts}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </CardContent>
    </Card>
  );

  // --- UI for Contacts List (Group Details) ---
  const renderContactsUI = () => (
    <Card sx={{ ...acmePageSectionCardSx, boxShadow: 'none', mt: 0 }}>
      <CardContent sx={acmeBillingSectionContentSx}>
      <PageBlockHeader>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <AcmeButton
            variant="outline"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToGroups}
          >
            Back to Groups
          </AcmeButton>
          <PageBlockTitle>Contacts in Group: {groupName}</PageBlockTitle>
        </Box>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {selectedContacts.length > 0 && (
            <AcmeButton
              variant="outline"
              onClick={() => {
                setConfirmDeleteDialog({
                  open: true,
                  type: 'contacts',
                  message: `Are you sure you want to delete ${selectedContacts.length} selected contacts from the group "${groupName}"? This action cannot be undone.`,
                  groupId: null,
                  contactId: null,
                  contactEmail: null
                });
              }}
              sx={{ color: acme.colors.destructive, borderColor: acme.colors.destructive }}
            >
              Delete Selected ({selectedContacts.length})
            </AcmeButton>
          )}
          <AcmeButton
            variant="default"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddEmailDialog(true)}
            disabled={loadingContacts}
          >
            New Contact
          </AcmeButton>
            <TableActionsMenuButton
              ariaLabel="More actions"
              onClick={(e) => setContactsMenuAnchor(e.currentTarget)}
            />
            <Menu
            anchorEl={contactsMenuAnchor}
            open={Boolean(contactsMenuAnchor)}
            onClose={() => setContactsMenuAnchor(null)}
          >
            <AcmeMenuItem
              onClick={() => {
                setContactsMenuAnchor(null);
                setImportType('contacts');
                setImportMethodDialog(true);
              }}
            >
              <CloudUploadIcon fontSize="small" sx={{ mr: 1.5, color: acme.colors.iconDefault }} />
              Import Contacts
            </AcmeMenuItem>
            <AcmeMenuItem
              onClick={() => {
                setContactsMenuAnchor(null);
                handleExportContacts();
              }}
            >
              <FileDownloadIcon fontSize="small" sx={{ mr: 1.5, color: acme.colors.iconDefault }} />
              Export Contacts
            </AcmeMenuItem>
          </Menu>
          <input 
            id="file-input-contacts"
            type="file" 
            accept=".xlsx, .xls, .csv, .txt" 
            hidden 
            onChange={handleFileSelectGroupImport} 
          />
        </Box>
      </PageBlockHeader>
      
      <Box mb={2} display="flex">
        <TextField label="Search Email" value={search} onChange={handleSearchChange} fullWidth />
      </Box>

      {loadingContacts ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : (
          <Table containerSx={{ mt: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                  indeterminate={selectedContacts.length > 0 && selectedContacts.length < filteredContacts.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedContacts(filteredContacts.map(contact => contact._id));
                    } else {
                      setSelectedContacts([]);
                    }
                  }}
                />
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Validity</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>
                <TextField
                  name="email"
                  label="Filter by Email"
                  value={filter.email}
                  onChange={(e) => setFilter({ ...filter, email: e.target.value })}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField
                  name="status"
                  label="Filter by Status"
                  value={filter.status}
                  onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                  variant="outlined"
                  size="small"
                  select
                  fullWidth
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="subscribed">Subscribed</MenuItem>
                  <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
                </TextField>
              </TableCell>
              <TableCell>
                <TextField
                  name="validity"
                  label="Filter by Validity"
                  value={filter.validity}
                  onChange={(e) => {
                    setFilter({ ...filter, validity: e.target.value });
                    setContactsPage(0);
                  }}
                  variant="outlined"
                  size="small"
                  select
                  fullWidth
                >
                  {VALIDITY_FILTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedContacts.length > 0 ? (
              paginatedContacts.map(contact => {
                const isSelected = selectedContacts.includes(contact._id);
                // Исправляем логику рейтинга: если нет рейтинга, показываем 0 звезд
                const contactRating = contact.rating !== null && contact.rating !== undefined 
                  ? contact.rating 
                  : 0; // Показываем 0 звезд если рейтинг не задан
                  
                return (
                  <TableRow key={contact._id} sx={{ backgroundColor: isSelected ? acme.colors.muted : 'inherit' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (e.target.checked) {
                            setSelectedContacts(prev => [...prev, contact._id]);
                          } else {
                            setSelectedContacts(prev => prev.filter(id => id !== contact._id));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>{contact.email}</TableCell>
                    <TableCell>
                      <Select
                        value={contact.status}
                        onChange={(e) => updateContactStatus(contact._id, e.target.value)}
                        size="small"
                        disabled={!isAdminRole(userRole)}
                        style={{
                          color: contact.status === 'unsubscribed' ? 'red' : 'black',
                          fontWeight: contact.status === 'unsubscribed' ? 'bold' : 'normal',
                          opacity: !isAdminRole(userRole) ? 0.7 : 1,
                        }}
                      >
                        <MenuItem value="subscribed">Subscribed</MenuItem>
                        <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          contact.validityReason
                            ? `${contact.validity}: ${contact.validityReason}`
                            : contact.validity || 'pending'
                        }
                      >
                        <span
                          style={{
                            color:
                              contact.validity === 'valid'
                                ? acme.colors.brand
                                : contact.validity === 'invalid' ||
                                    contact.validity === 'suppressed'
                                  ? acme.colors.destructive
                                  : contact.validity === 'risky'
                                    ? '#ed6c02'
                                    : acme.colors.mutedForegroundSolid,
                            fontWeight:
                              contact.validity === 'valid' ? 600 : 400,
                          }}
                        >
                          {contact.validity || 'pending'}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip 
                        title={`Email Quality Rating: ${contactRating}/5 stars. 
                        Based on: technical validity (DNS, syntax), 
                        behavioral activity (opens, clicks, deliverability).
                        ${contactRating === 0 ? 'Press the "Update Ratings" button to calculate.' : ''}`} 
                        arrow
                        placement="top"
                      >
                        <Box>
                          <Rating
                            name={`rating-${contact._id}`}
                            value={contactRating}
                            readOnly
                            size="small"
                            precision={0.5}
                            sx={{
                              '& .MuiRating-iconFilled': {
                                color: contactRating >= 4 ? '#4caf50' : contactRating >= 3 ? '#ffc107' : '#ff5722',
                              },
                              '& .MuiRating-iconEmpty': {
                                color: '#e0e0e0',
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', marginLeft: 0.5 }}>
                            ({contactRating.toFixed(1)})
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell onClick={stopTableRowClick}>
                      <TableActionsMenuButton
                        ariaLabel="Contact actions"
                        onClick={(event) => openContactRowMenu(event, contact)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableEmptyRow
                colSpan={6}
                icon={EmailIcon}
                title="No contacts in this group"
                description="Start building your contact list by adding email addresses manually or importing them from an Excel file. Once added, you can manage subscription status and organize contacts for email campaigns."
                hint='Use "New Contact" or "Import Contacts" above to get started.'
              />
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={filteredContacts.length}
                page={contactsPage}
                onPageChange={(event, newPage) => setContactsPage(newPage)}
                rowsPerPage={contactsRowsPerPage}
                onRowsPerPageChange={(event) => {
                  const newVal = parseInt(event.target.value, 10);
                  setContactsRowsPerPage(newVal);
                  setContactsPage(0);
                }}
                rowsPerPageOptions={[20, 50, 100, 200, 500]}
                labelRowsPerPage="Rows per page"
              />
            </TableRow>
          </TableFooter>
      </Table>

      )}

     <Box mt={2} display="flex" justifyContent="center">
  <Pagination 
    count={Math.ceil(filteredContacts.length / contactsRowsPerPage)} 
    page={contactsPage + 1}  // Pagination использует 1-индексацию
    onChange={(e, value) => setContactsPage(value - 1)} 
  />
</Box>

      <Dialog open={openAddEmailDialog} onClose={() => setOpenAddEmailDialog(false)}>
        <DialogTitle>Add Emails to Group</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Emails (separated by commas)"
            value={newEmails}
            onChange={(e) => setNewEmails(e.target.value)}
            placeholder="example1@mail.com, example2@mail.com"
          />
        </DialogContent>
        <DialogActions>
          <TextCancelButton onClick={() => setOpenAddEmailDialog(false)} disabled={loadingAddEmails}>Cancel</TextCancelButton>
          <BrandButton onClick={handleAddEmails} disabled={loadingAddEmails}>
            {loadingAddEmails ? <CircularProgress size={24} /> : 'Add'}
          </BrandButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openImportGroupDialog} onClose={() => setOpenImportGroupDialog(false)}>
        <DialogTitle>Import Contacts to Group</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to import {importedGroupContacts.length} contacts into group "{groupName}".
          </Typography>
          
          {/* Progress Bar */}
          {showProgress && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {importMessage}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {importProgress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={importProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: acme.colors.brand,
                    borderRadius: 4
                  }
                }} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <TextCancelButton onClick={() => setOpenImportGroupDialog(false)} disabled={loadingImport}>Cancel</TextCancelButton>
          <BrandButton onClick={handleImportGroupSubmit} disabled={loadingImport}>
            {loadingImport ? <CircularProgress size={24} /> : 'Import'}
          </BrandButton>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteDialog.open} onClose={() => setConfirmDeleteDialog({open: false, type: null, message: '', groupId: null, contactId: null, contactEmail: null})}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <Typography>{confirmDeleteDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDeleteDialog({open: false, type: null, message: '', groupId: null, contactId: null, contactEmail: null})} 
            disabled={loadingGroups || loadingContacts}
          >
            Cancel
          </Button>
          <Button 
            color="error" 
            onClick={() => {
              if (confirmDeleteDialog.type === 'groups') {
                handleDeleteSelectedGroups();
              } else if (confirmDeleteDialog.type === 'contacts') {
                handleDeleteSelectedContacts();
              } else if (confirmDeleteDialog.type === 'contact') {
                confirmDeleteContact();
              } else if (confirmDeleteDialog.type === 'group') {
                handleDeleteGroup();
              }
              setConfirmDeleteDialog({open: false, type: null, message: '', groupId: null, contactId: null, contactEmail: null});
            }} 
            disabled={loadingGroups || loadingContacts}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </CardContent>
    </Card>
  );

  return (
    <PageShell>
      <PageHeader
        title={groupName ? groupName : 'Contact Groups'}
        description={
          groupName
            ? 'View and manage contacts in this group. Import lists, add subscribers manually, or export contacts.'
            : 'Organize email contacts into groups for targeted campaigns. Import lists or add subscribers manually.'
        }
        sx={{ mb: 2 }}
      />
      {/* Global Progress Bar for Large Imports */}
      {showProgress && importProgress > 0 && importProgress < 100 && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            p: 2
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              {importMessage}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              {importProgress}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={importProgress} 
            sx={{ 
              height: 10, 
              borderRadius: 5,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: acme.colors.brand,
                borderRadius: 5
              }
            }} 
          />
        </Box>
      )}

      {groupName ? renderContactsUI() : renderGroupsUI()}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingGroups || loadingContacts || loadingAddEmails || loadingImport}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <PageSnackbars {...snackbarProps} />

      {/* Import Method Selection Dialog */}
      <Dialog 
        open={importMethodDialog} 
        onClose={() => setImportMethodDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          Choose Import Method
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 4 }}>
          <Typography variant="body2" sx={{ textAlign: 'center', mb: 4, color: '#666' }}>
            Select how you would like to import your contacts
          </Typography>
          
          <Grid container spacing={3}>
            {/* File Import Option */}
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  cursor: 'pointer',
                  border: '2px solid transparent',
                  '&:hover': {
                    border: `2px solid ${acme.colors.foreground}`,
                    backgroundColor: acme.colors.muted
                  },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
                onClick={() => handleImportMethodSelect('file', importType)}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: acme.colors.foreground, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Upload File
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Import contacts from Excel, CSV, or TXT file
                </Typography>
                <Typography variant="caption" sx={{ color: acme.colors.mutedForegroundSolid }}>
                  Supports: .xlsx, .xls, .csv, .txt
                </Typography>
              </Card>
            </Grid>

            {/* Copy & Paste Option */}
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  cursor: 'pointer',
                  border: '2px solid transparent',
                  '&:hover': {
                    border: `2px solid ${acme.colors.foreground}`,
                    backgroundColor: acme.colors.muted
                  },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
                onClick={() => handleImportMethodSelect('paste', importType)}
              >
                <ContentPasteIcon sx={{ fontSize: 48, color: acme.colors.foreground, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Copy & Paste
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Paste email addresses directly from your clipboard
                </Typography>
                <Typography variant="caption" sx={{ color: acme.colors.mutedForegroundSolid }}>
                  Separate emails with commas, semicolons, or new lines
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3 }}>
          <TextCancelButton onClick={() => setImportMethodDialog(false)}>
            Cancel
          </TextCancelButton>
        </DialogActions>
      </Dialog>

      {/* Paste Import Dialog */}
      <Dialog 
        open={pasteImportDialog} 
        onClose={() => setPasteImportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Paste Email Addresses
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            Paste your email addresses below. You can separate them with:
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Supported formats:
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              • One email per line
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              • Comma separated: email1@example.com, email2@example.com
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              • Semicolon separated: email1@example.com; email2@example.com
            </Typography>
          </Box>

          <TextField
            autoFocus
            fullWidth
            multiline
            rows={10}
            variant="outlined"
            label="Paste email addresses here"
            placeholder="example1@email.com
example2@email.com
example3@email.com"
            value={pasteData}
            onChange={(e) => setPasteData(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <Typography variant="caption" sx={{ color: acme.colors.mutedForegroundSolid }}>
            Tip: You can paste directly from Excel, Google Sheets, or any other source
          </Typography>
        </DialogContent>
        <DialogActions>
          <TextCancelButton onClick={() => setPasteImportDialog(false)}>
            Cancel
          </TextCancelButton>
          <BrandButton 
            primaryHover="light"
            onClick={handlePasteImport}
            variant="contained"
          >
            Import Contacts
          </BrandButton>
        </DialogActions>
      </Dialog>

      <TableActionsMenu
        anchorEl={groupRowMenu.anchorEl}
        open={Boolean(groupRowMenu.anchorEl)}
        onClose={closeGroupRowMenu}
      >
        <AcmeMenuItem onClick={() => handleGroupRowMenuItem('reverify')}>
          <StarIcon fontSize="small" sx={{ mr: 1.5, color: acme.colors.iconDefault }} />
          Re-verify group
        </AcmeMenuItem>
        <AcmeMenuItem onClick={() => handleGroupRowMenuItem('rename')}>
          Rename
        </AcmeMenuItem>
        <AcmeMenuItem
          onClick={() => handleGroupRowMenuItem('delete')}
          sx={destructiveMenuItemSx}
        >
          Delete
        </AcmeMenuItem>
      </TableActionsMenu>

      <TableActionsMenu
        anchorEl={contactRowMenu.anchorEl}
        open={Boolean(contactRowMenu.anchorEl)}
        onClose={closeContactRowMenu}
      >
        <AcmeMenuItem onClick={() => handleContactRowMenuItem('rename')}>
          Rename
        </AcmeMenuItem>
        <AcmeMenuItem
          onClick={() => handleContactRowMenuItem('delete')}
          sx={destructiveMenuItemSx}
        >
          Delete
        </AcmeMenuItem>
      </TableActionsMenu>

      <Dialog open={renameGroupDialog.open} onClose={closeRenameGroupDialog}>
        <DialogTitle>Rename Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Group Name"
            value={renameGroupDialog.newName}
            onChange={(e) =>
              setRenameGroupDialog((prev) => ({ ...prev, newName: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <TextCancelButton onClick={closeRenameGroupDialog} disabled={renamingGroup}>
            Cancel
          </TextCancelButton>
          <BrandButton onClick={handleRenameGroup} disabled={renamingGroup}>
            {renamingGroup ? <CircularProgress size={24} /> : 'Rename'}
          </BrandButton>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ContactList;
